import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";
import WalletPaymentDialog from "../WalletPaymentDialog";

const PaymentRequestPayForAllMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  isCurrentUserOwnerOrTeamMember,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  getCurrencySymbol,
  formattedMessage,
  wrappedOnOpenPaymentDialog,
  walletPaymentDialogOpen,
  handleCloseWalletPaymentDialog,
  handleSendManualPaymentRequest,
}) => {
  const currentUserId = currentUser?.userId ?? storedUserId;
  const orderId = message?.orderId;
  const userOrders = orderId?.groupOrder?.userOrders || [];
  const currentUserOrder = userOrders.find((userOrder) => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === currentUserId;
  });

  const allUpfrontPaymentsComplete =
    orderId?.paymentCompleted === true ||
    (orderId?.totalPaidAmount > 0 && orderId?.paymentStatus === "paid");

  const initiatorId = message?.orderId?.paymentFlow?.initiatedBy;
  const upfrontPercentage = orderId?.paymentSettings?.upfrontPercentage || 20;
  const groupSubtotal = orderId?.groupSubtotal || 0;
  const requiredUpfrontAmount = (groupSubtotal * upfrontPercentage) / 100;
  const totalPaidAmount = orderId?.totalPaidAmount || 0;
  const remainingUpfrontPayment = requiredUpfrontAmount - totalPaidAmount;
  const hasRemainingUpfrontPayment = remainingUpfrontPayment > 0.01;

  const initiatorOrder = userOrders.find((userOrder) => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === initiatorId;
  });
  const initiatorRemainingAmount = initiatorOrder?.remainingAmount || 0;
  const hasInitiatorRemainingPayment = initiatorRemainingAmount > 0.01;

  const customMessageText = formattedMessage.text;
  const isInitiator = initiatorId === currentUserId;
  
  let shouldShowPayButton = false;
  if (message?.buttonDisabled === true || isCurrentUserOwnerOrTeamMember) {
    shouldShowPayButton = false;
  } else {
    shouldShowPayButton =
      isInitiator &&
      (currentUserOrder || isInitiator) &&
      !allUpfrontPaymentsComplete &&
      (hasRemainingUpfrontPayment ||
        hasInitiatorRemainingPayment ||
        (currentUserOrder &&
          currentUserOrder.paymentStatus !== "paid" &&
          currentUserOrder.paymentStatus !== "upfront_paid"));
  }

  const isMessagePaid =
    currentUserOrder &&
    currentUserOrder.amountToPay > 0 &&
    !hasRemainingUpfrontPayment &&
    !hasInitiatorRemainingPayment &&
    (currentUserOrder.paymentStatus === "paid" ||
      (currentUserOrder.paymentStatus === "upfront_paid" &&
        !hasRemainingUpfrontPayment));

  return (
    <>
      <MessageBubbleContainer isOwn={isOwn}>
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
              mb: 0.5,
              alignSelf: "flex-start",
            }}
          >
            {message.senderId?.fullName || message.senderId?.userName || "user"}
          </Typography>
        )}

        <MessageContent
          isOwn={isOwn}
          isTemp={message.isTemp}
          isOrder={false}
          isPayment={true}
          isRequest={false}
          idDraft={false}
          isDisabled={false}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: "280px",
              maxWidth: "380px",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            <Box
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {customMessageText || "Payment request"}
            </Box>

            {shouldShowPayButton && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Button
                  variant="contained"
                  disabled={isMessagePaid || message?.buttonDisabled === true}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (message?.buttonDisabled !== true && message?.orderId?._id) {
                      wrappedOnOpenPaymentDialog(message);
                    }
                  }}
                  sx={{
                    backgroundColor:
                      isMessagePaid || message?.buttonDisabled === true
                        ? "#9CA3AF"
                        : isOwn
                          ? "#ffffff"
                          : "#7C3AED",
                    color:
                      isMessagePaid || message?.buttonDisabled === true
                        ? "white"
                        : isOwn
                          ? "#7C3AED"
                          : "#ffffff",
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    cursor:
                      isMessagePaid || message?.buttonDisabled === true
                        ? "not-allowed"
                        : "pointer",
                    "&:hover": {
                      backgroundColor:
                        isMessagePaid || message?.buttonDisabled === true
                          ? "#9CA3AF"
                          : isOwn
                            ? "#f3f4f6"
                            : "#6d28d9",
                    },
                    "&:disabled": {
                      backgroundColor: "#9CA3AF",
                      color: "white",
                    },
                  }}
                >
                  {isMessagePaid
                    ? "paid"
                    : (() => {
                        const amount =
                          message?.amount ||
                          orderId?.groupSubtotal ||
                          0;
                        const currencyCode = orderId?.currency || "USD";
                        const currencySymbol =
                          getCurrencySymbol(currencyCode);
                        return amount > 0
                          ? `pay ${currencySymbol}${Number(amount).toFixed(2)}`
                          : "pay now";
                      })()}
                </Button>
              </Box>
            )}
          </Box>
        </MessageContent>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isOwn ? "flex-end" : "flex-start",
            gap: 1,
            mt: 1,
            width: "100%",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.timestamp}
          </Typography>

          {isOwn && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(0,0,0,0.6)",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              {message.isTemp ? (
                <AccessTime fontSize="inherit" />
              ) : (
                getReadReceiptStatus() || "✓"
              )}
            </Typography>
          )}
        </Box>
      </MessageBubbleContainer>

      <WalletPaymentDialog
        open={walletPaymentDialogOpen}
        onClose={handleCloseWalletPaymentDialog}
        onSendManualPayment={handleSendManualPaymentRequest}
      />
    </>
  );
};

export default PaymentRequestPayForAllMessage;
